/**
 * On-Device Reminder Logs (FEAT-REMINDERS-LOG-001).
 *
 * A self-contained page — independent of the Delivery Logs page. It reads ONLY the new
 * `GET /reminders/logs` endpoint (per-tenant `tbl_local_notification_logs`) and shows each
 * on-device notification occurrence with its served→clicked delivery tracking. No code,
 * endpoint, or permission is shared with src/pages/MessageLogs.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, HStack, VStack, Flex, Text, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Input, Select, Button, ButtonGroup, Icon, Tag, Spinner, Center, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { LuBell, LuBellRing, LuSmartphone, LuCheckCheck, LuMousePointerClick } from 'react-icons/lu';
import PageHeading from '../../components/shared/PageHeading';
import moment from 'moment-timezone';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import { fetchWrapper } from '../../helpers/fetch-wrapper';
import usePagePermissions from '../../hooks/usePagePermissions';
import { getUserTimeZone } from '../../utils/DefaultFunction';

// Status vocabulary for local notifications (reuses tbl_status codes; self-contained here).
const STATUS = {
  200: { key: 'served', label: 'Served', scheme: 'green', Icon: LuCheckCheck },
  301: { key: 'clicked', label: 'Clicked', scheme: 'blue', Icon: LuMousePointerClick },
};
const statusOf = (id) => STATUS[id] || { key: 'unknown', label: 'Unknown', scheme: 'gray', Icon: LuBell };
const platformLabel = (p) => (p ? String(p).charAt(0).toUpperCase() + String(p).slice(1) : '—');

const FIRST_PAGE = { cursor: null, direction: 'next' };

export default function ReminderLogs({ header }) {
  const { loading: permsLoading, can } = usePagePermissions('on-device-reminder-logs');
  const listPermission = can('odrl-list');

  const TZ = useMemo(() => getUserTimeZone(), []);
  const fmt = useCallback((d) => (d ? moment.utc(d).tz(TZ).format('DD MMM YY hh:mm:ss A') : '—'), [TZ]);

  const [list, setList] = useState([]);
  const [paging, setPaging] = useState({ has_next: false, has_prev: false, next_cursor: null, prev_cursor: null });
  const [counts, setCounts] = useState({ servedCount: 0, clickedCount: 0 });
  const [loading, setLoading] = useState(true);

  // filters (temporary → applied on Apply)
  const [status, setStatus] = useState('');
  const [platform, setPlatform] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [applied, setApplied] = useState({ status: '', platform: '', startDate: '', endDate: '' });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageRequest, setPageRequest] = useState(FIRST_PAGE);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState(null);

  // debounce search → reset to first page
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPageRequest(FIRST_PAGE); }, 500);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('cursor', pageRequest.cursor || '');
      p.append('direction', pageRequest.direction || 'next');
      if (applied.status) p.append('status', applied.status);
      if (applied.platform) p.append('platform', applied.platform);
      if (applied.startDate) p.append('start_date', moment(applied.startDate).format('YYYY-MM-DD'));
      if (applied.endDate) p.append('end_date', moment(applied.endDate).format('YYYY-MM-DD'));
      if (debouncedSearch) p.append('search', debouncedSearch);
      const url = `${process.env.REACT_APP_API_URL}/reminders/logs?${p.toString()}&pageSize=50&tz=${TZ}`;
      const data = await fetchWrapper.get(url);
      setList(Array.isArray(data?.data) ? data.data : []);
      setPaging(data?.paging || { has_next: false, has_prev: false });
      setCounts({ servedCount: data?.servedCount || 0, clickedCount: data?.clickedCount || 0 });
    } catch (e) {
      console.error('reminder logs load:', e);
      setList([]); setPaging({ has_next: false, has_prev: false });
    } finally { setLoading(false); }
  }, [applied, debouncedSearch, pageRequest, TZ]);

  useEffect(() => {
    if (!permsLoading && listPermission) load();
  }, [permsLoading, listPermission, load]);

  const applyFilters = () => { setApplied({ status, platform, startDate, endDate }); setPageRequest(FIRST_PAGE); };
  const clearFilters = () => { setStatus(''); setPlatform(''); setStartDate(''); setEndDate(''); setSearch(''); setApplied({ status: '', platform: '', startDate: '', endDate: '' }); setPageRequest(FIRST_PAGE); };
  const openJourney = (row) => { setSelected(row); onOpen(); };

  const StatusPill = ({ id }) => {
    const s = statusOf(id);
    return (
      <HStack display="inline-flex" borderRadius="full" border="1px solid" borderColor={`${s.scheme}.200`} bg={`${s.scheme}.50`} px={2} py={0.5} spacing={1.5}>
        <Box w="6px" h="6px" borderRadius="full" bg={`${s.scheme}.500`} />
        <Text fontSize="xs" fontWeight="600" color={`${s.scheme}.700`}>{s.label}</Text>
      </HStack>
    );
  };

  if (permsLoading || loading) {
    return (<><Header header={header} /><BoxWrapper><Center py={16}><VStack><Spinner size="xl" thickness="4px" color="var(--brand-primary)" /><Text color="#64748b">Loading notification logs…</Text></VStack></Center></BoxWrapper></>);
  }
  if (!listPermission) {
    return (<><Header header={header} /><BoxWrapper><Center py={16}><VStack><Text fontSize="lg" fontWeight="700">No Access</Text><Text color="#64748b">You don't have permission to view this section.</Text></VStack></Center></BoxWrapper></>);
  }

  return (
    <>
      <Header header={header} />
      <BoxWrapper>
        {/* Toolbar */}
        <PageHeading
          icon={LuBellRing}
          title="On-Device Reminder Logs"
          subtitle={
            // span-based HStack: the subtitle renders inside a <p>.
            <HStack as="span" spacing={2} mt={1}>
              <Tag size="sm" colorScheme="green" variant="subtle">Served {counts.servedCount}</Tag>
              <Tag size="sm" colorScheme="blue" variant="subtle">Clicked {counts.clickedCount}</Tag>
            </HStack>
          }
          mb={4}
        >
          <HStack spacing={2}>
            <Input size="sm" w="260px" placeholder="Search by Message ID, recipient, device id" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Tooltip label="Refresh"><Button size="sm" onClick={load} leftIcon={<RepeatIcon />} variant="outline">Refresh</Button></Tooltip>
          </HStack>
        </PageHeading>

        {/* Filters */}
        <Flex gap={3} wrap="wrap" align="flex-end" mb={4} p={3} bg="#f8fafc" borderRadius="12px">
          <Box><Text fontSize="xs" color="#64748b" mb={1}>Status</Text>
            <Select size="sm" w="150px" bg="white" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option><option value="served">Served</option><option value="clicked">Clicked</option>
            </Select></Box>
          <Box><Text fontSize="xs" color="#64748b" mb={1}>Platform</Text>
            <Select size="sm" w="150px" bg="white" value={platform} onChange={(e) => setPlatform(e.target.value)}>
              <option value="">All</option><option value="android">Android</option><option value="ios">iOS</option>
            </Select></Box>
          <Box><Text fontSize="xs" color="#64748b" mb={1}>Start date</Text><Input size="sm" w="160px" bg="white" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></Box>
          <Box><Text fontSize="xs" color="#64748b" mb={1}>End date</Text><Input size="sm" w="160px" bg="white" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></Box>
          <Button size="sm" colorScheme="blue" onClick={applyFilters}>Apply</Button>
          <Button size="sm" variant="ghost" onClick={clearFilters}>Clear</Button>
        </Flex>

        {/* Table */}
        <TableContainer>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Message ID</Th><Th>Recipient</Th><Th>Reminder (rule)</Th><Th>Platform</Th>
                <Th>Date &amp; Time</Th><Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {list.length === 0 ? (
                <Tr><Td colSpan={6}><Center py={10}><Text color="#94a3b8">No on-device notification logs for this filter.</Text></Center></Td></Tr>
              ) : list.map((row) => (
                <Tr key={row._id || row.message_id} _hover={{ bg: '#f8fafc' }}>
                  <Td>
                    <Text color="var(--brand-primary)" cursor="pointer" fontWeight="600" _hover={{ textDecoration: 'underline' }} onClick={() => openJourney(row)}>
                      {String(row.message_id || '').slice(0, 8)}… <ExternalLinkIcon boxSize={3} />
                    </Text>
                  </Td>
                  <Td>{row.contact || row.distinct_id || '—'}</Td>
                  <Td><Text fontFamily="mono" fontSize="xs">{row.template_id || '—'}</Text></Td>
                  <Td><Tag size="sm" variant="subtle" colorScheme={row.platform === 'ios' ? 'purple' : 'teal'}><Icon as={LuSmartphone} boxSize={3} mr={1} />{platformLabel(row.platform)}</Tag></Td>
                  <Td>
                    <VStack align="flex-start" spacing={0} color="#64748b" fontSize="xs">
                      <Text>Received: {fmt(row.receive_date)}</Text>
                      {row.served_at && <Text>Served: {fmt(row.served_at)}</Text>}
                      {row.clicked_at && <Text>Clicked: {fmt(row.clicked_at)}</Text>}
                    </VStack>
                  </Td>
                  <Td><StatusPill id={row.status_id} /></Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex justify="space-between" align="center" mt={4}>
          <Text fontSize="sm" color="#64748b">Showing {list.length} notification{list.length === 1 ? '' : 's'}</Text>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button leftIcon={<ChevronLeftIcon />} isDisabled={!paging.has_prev || loading} onClick={() => setPageRequest({ cursor: paging.prev_cursor, direction: 'prev' })}>Previous</Button>
            <Button rightIcon={<ChevronRightIcon />} isDisabled={!paging.has_next || loading} onClick={() => setPageRequest({ cursor: paging.next_cursor, direction: 'next' })}>Next</Button>
          </ButtonGroup>
        </Flex>
      </BoxWrapper>

      {/* Delivery journey modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Notification Delivery Journey</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selected && (
              <VStack align="stretch" spacing={4}>
                <Box bg="#f8fafc" borderRadius="12px" p={3}>
                  <HStack justify="space-between" mb={2}><Text fontSize="xs" color="#64748b">Message ID</Text><Text fontFamily="mono" fontSize="xs">{selected.message_id}</Text></HStack>
                  <HStack justify="space-between" mb={2}><Text fontSize="xs" color="#64748b">Reminder (rule)</Text><Text fontSize="xs">{selected.template_id || '—'}</Text></HStack>
                  <HStack justify="space-between" mb={2}><Text fontSize="xs" color="#64748b">Recipient</Text><Text fontSize="xs">{selected.contact || selected.distinct_id || '—'}</Text></HStack>
                  <HStack justify="space-between"><Text fontSize="xs" color="#64748b">Platform</Text><Text fontSize="xs">{platformLabel(selected.platform)}{selected.app_version ? ` · v${selected.app_version}` : ''}</Text></HStack>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="700" mb={3}>Delivery timeline</Text>
                  <VStack align="stretch" spacing={0}>
                    {[...(selected.history || [])].sort((a, b) => new Date(a.time) - new Date(b.time)).map((h, i, arr) => {
                      const s = statusOf(h.status_id);
                      return (
                        <HStack key={i} align="flex-start" spacing={3}>
                          <VStack spacing={0}>
                            <Flex w="28px" h="28px" borderRadius="full" bg={`${s.scheme}.50`} border="1px solid" borderColor={`${s.scheme}.300`} align="center" justify="center"><Icon as={s.Icon} boxSize={3.5} color={`${s.scheme}.600`} /></Flex>
                            {i < arr.length - 1 && <Box w="2px" h="24px" bg="#e2e8f0" />}
                          </VStack>
                          <Box pb={i < arr.length - 1 ? 4 : 0}>
                            <Text fontSize="sm" fontWeight="600" color={`${s.scheme}.700`}>{s.label}</Text>
                            <Text fontSize="xs" color="#64748b">{fmt(h.time)}</Text>
                          </Box>
                        </HStack>
                      );
                    })}
                    {(!selected.history || selected.history.length === 0) && <Text fontSize="sm" color="#94a3b8">No delivery events recorded.</Text>}
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
